import React from 'react';
import { FaFileInvoiceDollar, FaPlus, FaTrash, FaInfoCircle } from 'react-icons/fa';
import OfferService, { PaymentMilestone, PaymentPlanTemplate, PaymentPlan } from '../services/OfferService';

const labelStyle: React.CSSProperties = {
  display: 'block',
  marginBottom: '6px',
  fontSize: '13px',
  fontWeight: 600,
  color: '#444',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  border: '1px solid #e0e3ef',
  borderRadius: '6px',
  fontSize: '14px',
  boxSizing: 'border-box',
};

const thStyle: React.CSSProperties = {
  padding: '10px 12px',
  textAlign: 'left',
  fontSize: '12px',
  fontWeight: 600,
  color: '#666',
  whiteSpace: 'nowrap',
};

const tdStyle: React.CSSProperties = {
  padding: '10px 12px',
  fontSize: '14px',
  color: '#333',
  verticalAlign: 'top',
};

interface PaymentPlanSectionProps {
  isEdit: boolean;
  paymentPlanEnabled: boolean;
  onPaymentPlanEnabledChange: (enabled: boolean) => void;
  paymentPlanTemplate: string;
  onPaymentPlanTemplateChange: (template: string) => void;
  paymentMilestones: PaymentMilestone[];
  existingPaymentPlan: PaymentPlan | null;
  paymentPlanTemplates: PaymentPlanTemplate[];
  loadingTemplates: boolean;
  selectedTemplateId: number | null;
  onTemplatePreviewSelect: (templateId: number) => void;
  onSetSelectedTemplateId: (id: number | null) => void;
  onApplyTemplate: (templateId: number) => void;
  previewTemplate: PaymentPlanTemplate | null;
  onSetPreviewTemplate: (t: PaymentPlanTemplate | null) => void;
  loadingPreview: boolean;
  onAddMilestone: () => void;
  onRemoveMilestone: (index: number) => void;
  onMilestoneChange: (index: number, field: keyof PaymentMilestone, value: any) => void;
  getMilestonePercentageSum: () => number;
  isPaymentPlanValid: () => boolean;
  formatMilestonePreview: (milestones: { label: string; percentage: number }[]) => string;
  totalGross: number;
  minAmount: number;
  formatCurrency: (amount: number) => string;
}

export default function PaymentPlanSection({
  isEdit,
  paymentPlanEnabled,
  onPaymentPlanEnabledChange,
  paymentPlanTemplate,
  onPaymentPlanTemplateChange,
  paymentMilestones,
  existingPaymentPlan,
  paymentPlanTemplates,
  loadingTemplates,
  selectedTemplateId,
  onTemplatePreviewSelect,
  onSetSelectedTemplateId,
  onApplyTemplate,
  previewTemplate,
  onSetPreviewTemplate,
  loadingPreview,
  onAddMilestone,
  onRemoveMilestone,
  onMilestoneChange,
  getMilestonePercentageSum,
  isPaymentPlanValid,
  formatMilestonePreview,
  totalGross,
  minAmount,
  formatCurrency,
}: PaymentPlanSectionProps): React.JSX.Element | null {
  if (!(totalGross >= minAmount || paymentPlanEnabled || existingPaymentPlan)) {
    return null;
  }

  return (
    <div
      style={{
        background: 'white',
        border: '1px solid #e0e3ef',
        borderRadius: '12px',
        padding: '24px',
        marginTop: '24px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
        <FaFileInvoiceDollar size={18} color="#059669" />
        <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#021a4a' }}>
          Payment Plan
        </h3>
        <span style={{ fontSize: '13px', color: '#666' }}>(optional)</span>
        {totalGross >= minAmount && !paymentPlanEnabled && (
          <span
            style={{
              fontSize: '11px',
              padding: '2px 8px',
              background: '#dbeafe',
              color: '#1e40af',
              borderRadius: '10px',
              fontWeight: 600,
            }}
          >
            Recommended
          </span>
        )}
      </div>
      <p style={{ margin: '0 0 16px 0', fontSize: '14px', color: '#666' }}>
        Define a payment schedule for this project. Recommended for jobs over {OfferService.formatCurrency(minAmount)}.
      </p>

      <label
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          cursor: 'pointer',
          fontSize: '14px',
          color: '#333',
        }}
      >
        <input
          type="checkbox"
          checked={paymentPlanEnabled}
          onChange={(e) => onPaymentPlanEnabledChange(e.target.checked)}
          style={{ width: '18px', height: '18px', cursor: 'pointer' }}
        />
        Use payment plan for this offer
      </label>

      {paymentPlanEnabled && (
        <div
          style={{
            marginTop: '20px',
            paddingTop: '20px',
            borderTop: '1px solid #e0e3ef',
          }}
        >
          {/* Template Picker with Preview (edit mode) */}
          {isEdit && paymentPlanTemplates.length > 0 && (
            <div style={{ marginBottom: '20px' }}>
              <label style={labelStyle}>Milestone-Template anwenden</label>
              <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                <select
                  data-testid="payment-plan-template-select"
                  value={selectedTemplateId ?? ''}
                  onChange={(e) => {
                    const v = parseInt(e.target.value, 10);
                    const templateId = Number.isFinite(v) ? v : null;
                    if (templateId) {
                      onTemplatePreviewSelect(templateId);
                    } else {
                      onSetSelectedTemplateId(null);
                      onSetPreviewTemplate(null);
                    }
                  }}
                  style={{ ...inputStyle, maxWidth: '450px' }}
                  disabled={loadingTemplates}
                >
                  <option value="">Template auswählen...</option>
                  {paymentPlanTemplates.map((template) => {
                    const milestoneCount = template.milestones_count ?? template.milestones?.length ?? 0;
                    return (
                      <option
                        key={template.template_id}
                        value={template.template_id}
                        data-testid={`payment-plan-template-option-${template.template_id}`}
                        data-milestones-count={milestoneCount}
                      >
                        {`${template.name} (${template.code}) — ${milestoneCount} Milestones`}
                      </option>
                    );
                  })}
                </select>
                {selectedTemplateId && (
                  <button
                    onClick={() => onApplyTemplate(selectedTemplateId)}
                    disabled={loadingPreview}
                    style={{
                      padding: '8px 16px',
                      background: '#021a4a',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '13px',
                      fontWeight: 500,
                      cursor: loadingPreview ? 'wait' : 'pointer',
                    }}
                  >
                    Anwenden
                  </button>
                )}
                {loadingTemplates && <span style={{ fontSize: '13px', color: '#666' }}>Lade...</span>}
              </div>

              {/* Template Preview */}
              {loadingPreview && (
                <div style={{ marginTop: '8px', fontSize: '13px', color: '#666' }}>
                  Lade Vorschau...
                </div>
              )}
              {previewTemplate && !loadingPreview && (
                <div
                  data-testid="payment-plan-template-preview"
                  style={{
                    marginTop: '8px',
                    padding: '10px 12px',
                    background: '#f0f4ff',
                    borderRadius: '6px',
                    fontSize: '13px',
                    color: '#1e40af',
                  }}
                >
                  <strong>Preview:</strong> {formatMilestonePreview(previewTemplate.milestones)}
                </div>
              )}

              <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: '#666' }}>
                Die Auswahl eines Templates ersetzt vorhandene Milestones.
              </p>
            </div>
          )}

          {/* Template Selector for new offers */}
          {!isEdit && (
            <div style={{ marginBottom: '20px' }}>
              <label style={labelStyle}>Template</label>
              <select
                value={paymentPlanTemplate}
                onChange={(e) => onPaymentPlanTemplateChange(e.target.value)}
                style={{ ...inputStyle, maxWidth: '350px' }}
              >
                <option value="none">None</option>
                <option value="50-50">50% down, 50% on completion</option>
                <option value="50-25-25">50% down, 25% rough installation, 25% final</option>
                <option value="custom">Custom</option>
              </select>
            </div>
          )}

          {/* Milestones Table */}
          {paymentMilestones.length > 0 && (
            <>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '700px' }}>
                  <thead>
                    <tr style={{ background: '#f8f9fc', borderBottom: '2px solid #e0e3ef' }}>
                      <th style={{ ...thStyle, width: '40px' }}>#</th>
                      <th style={{ ...thStyle, width: '180px' }}>Label</th>
                      <th style={{ ...thStyle, width: '120px' }}>Type</th>
                      <th style={{ ...thStyle, width: '80px', textAlign: 'right' }}>%</th>
                      <th style={{ ...thStyle, width: '150px' }}>Trigger</th>
                      <th style={{ ...thStyle, width: '100px', textAlign: 'right' }}>Due (days)</th>
                      {existingPaymentPlan && (
                        <th style={{ ...thStyle, width: '120px', textAlign: 'right' }}>Amount</th>
                      )}
                      <th style={{ ...thStyle, width: '50px' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {paymentMilestones.map((milestone, index) => (
                      <tr key={index} style={{ borderBottom: '1px solid #e0e3ef' }}>
                        <td style={tdStyle}>{milestone.sequence}</td>
                        <td style={tdStyle}>
                          <input
                            type="text"
                            value={milestone.label}
                            onChange={(e) => onMilestoneChange(index, 'label', e.target.value)}
                            style={{ ...inputStyle, padding: '6px 10px' }}
                          />
                        </td>
                        <td style={tdStyle}>
                          <select
                            value={milestone.milestone_type}
                            onChange={(e) => onMilestoneChange(index, 'milestone_type', e.target.value)}
                            style={{ ...inputStyle, padding: '6px 10px' }}
                          >
                            <option value="DOWN_PAYMENT">Down Payment</option>
                            <option value="PROGRESS">Progress</option>
                            <option value="FINAL">Final</option>
                          </select>
                        </td>
                        <td style={tdStyle}>
                          <input
                            type="text"
                            inputMode="numeric"
                            value={milestone.percentage}
                            onChange={(e) => {
                              const val = e.target.value.replace(/[^0-9.]/g, '');
                              const num = parseFloat(val);
                              if (val === '' || (!isNaN(num) && num >= 0 && num <= 100)) {
                                onMilestoneChange(index, 'percentage', val === '' ? 0 : num);
                              }
                            }}
                            onFocus={(e) => e.target.select()}
                            style={{ ...inputStyle, padding: '6px 10px', textAlign: 'right' }}
                          />
                        </td>
                        <td style={tdStyle}>
                          <select
                            data-testid={`milestone-trigger-select-${milestone.id || index}`}
                            value={milestone.trigger_type}
                            onChange={(e) => onMilestoneChange(index, 'trigger_type', e.target.value)}
                            style={{ ...inputStyle, padding: '6px 10px' }}
                          >
                            <option value="ON_OFFER_ACCEPTED">On Offer Accepted</option>
                            <option value="ON_PROJECT_STATUS">On Project Status</option>
                            <option value="ON_DATE">On Date</option>
                            <option value="MANUAL">Manual</option>
                            <option
                              value="ON_OPS_PROGRESS"
                              data-testid={`milestone-trigger-option-ops-${milestone.id || index}`}
                            >
                              Nach Jobs erledigt (Plantafel)
                            </option>
                          </select>
                        </td>
                        <td style={tdStyle}>
                          <input
                            type="text"
                            inputMode="numeric"
                            value={milestone.due_offset_days ?? ''}
                            onChange={(e) => {
                              const val = e.target.value.replace(/[^0-9]/g, '');
                              onMilestoneChange(index, 'due_offset_days', val === '' ? null : parseInt(val, 10));
                            }}
                            onFocus={(e) => e.target.select()}
                            placeholder="14"
                            style={{ ...inputStyle, padding: '6px 10px', textAlign: 'right' }}
                          />
                        </td>
                        {existingPaymentPlan && (
                          <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }}>
                            {milestone.amount_gross != null ? formatCurrency(milestone.amount_gross) : '-'}
                          </td>
                        )}
                        <td style={tdStyle}>
                          <button
                            onClick={() => onRemoveMilestone(index)}
                            title="Remove milestone"
                            style={{
                              background: 'none',
                              border: 'none',
                              padding: '6px',
                              cursor: 'pointer',
                              color: '#dc2626',
                              borderRadius: '4px',
                            }}
                          >
                            <FaTrash size={12} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Percentage Sum Validation */}
              <div
                style={{
                  marginTop: '8px',
                  padding: '8px 16px',
                  background: isPaymentPlanValid() ? '#f0fdf4' : '#fef2f2',
                  borderRadius: '6px',
                  fontSize: '13px',
                  color: isPaymentPlanValid() ? '#166534' : '#dc2626',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <FaInfoCircle size={14} />
                <span>
                  Summe: <strong>{getMilestonePercentageSum()}%</strong>
                  {!isPaymentPlanValid() && ' — Muss 100% ergeben'}
                </span>
              </div>

              {/* Add Milestone Button */}
              <button
                onClick={onAddMilestone}
                style={{
                  marginTop: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '8px 16px',
                  border: '1px dashed #ccc',
                  borderRadius: '6px',
                  background: 'white',
                  color: '#666',
                  fontSize: '13px',
                  cursor: 'pointer',
                }}
              >
                <FaPlus size={10} />
                Add milestone
              </button>

              {/* UX Hint for ON_OPS_PROGRESS trigger */}
              {paymentMilestones.some((m) => m.trigger_type === 'ON_OPS_PROGRESS') && (
                <div
                  style={{
                    marginTop: '16px',
                    padding: '12px 16px',
                    background: '#eff6ff',
                    borderRadius: '8px',
                    fontSize: '13px',
                    color: '#1e40af',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '8px',
                  }}
                >
                  <FaInfoCircle size={14} style={{ marginTop: '2px', flexShrink: 0 }} />
                  <span>
                    <strong>Hinweis:</strong> Milestones mit Trigger "Nach Jobs erledigt" werden automatisch auf "Bereit zur Rechnung" gesetzt, sobald alle zugeordneten Jobs/Checklisten in der Plantafel erledigt sind.
                  </span>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
